import { CaPageHeader, usePath, friendlyTime } from "@/company-admin/shared";
import { StatusPill } from "@/admin/ui";

export default function CaOverview() {
    const { data, loading, error } = usePath("/company-admin/overview");
    return (
        <div data-testid="ca-overview-page">
            <CaPageHeader
                title={data?.company?.name ? `${data.company.name}` : "Company Overview"}
                subtitle="Prepare your workspace, invite the team, and configure departments before employees start working."
            />

            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-6">{error}</div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 9 }).map((_, i) => <div key={i} className="glass rounded-3xl p-6 h-[130px] animate-pulse" />)}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(data?.cards || []).map((c, i) => (
                        <div key={c.key} data-testid={`ca-card-${c.key}`} className="glass rounded-3xl p-6 hover:-translate-y-1 transition-all duration-500">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">{c.label}</span>
                                <span className="font-mono-alt text-[10px] text-violet-500">/{String(i + 1).padStart(2, "0")}</span>
                            </div>
                            <div className="mt-4 font-display text-3xl font-medium text-black tracking-tight leading-none truncate">{c.value ?? "—"}</div>
                            <div className="mt-2 text-[12px] text-neutral-500">{c.hint}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-10 glass rounded-3xl p-6 sm:p-8">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">Recent Activity</div>
                        <div className="mt-1 font-display text-xl text-black">Company activity feed</div>
                    </div>
                </div>
                <ul className="mt-5 divide-y divide-black/[0.05]">
                    {(data?.recent_activity || []).map((r) => (
                        <li key={r.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                            <div className="min-w-0">
                                <div className="font-medium text-black truncate">
                                    {String(r.action || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                </div>
                                <div className="text-[12px] text-neutral-500 truncate">
                                    {r.user_name || r.user_email || "system"} · {r.module || "system"}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <StatusPill status={r.status || "success"} />
                                <span className="text-[11px] text-neutral-400">{friendlyTime(r.timestamp)}</span>
                            </div>
                        </li>
                    ))}
                    {(!data?.recent_activity || data.recent_activity.length === 0) && (
                        <li className="py-4 text-sm text-neutral-500">No activity yet — invite your first employee to get started.</li>
                    )}
                </ul>
            </div>
        </div>
    );
}
